import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import MainSection, { MainSectionParts } from '../../components/MainSection/MainSection'
import { SITE_URL } from '../../utils/constants'
import { cls } from '../../utils/utils'
import * as styles from './SearchPage.module.scss'
import { IData, IInfiniteScrollProps, IPage, ISearchItem, ISearchItemState, ISearchPageProps } from './SearchPage.types'
import { PagePreviewTiles } from './components/PagePreviewTiles/PagePreviewTiles'
import { useRestoreState } from './hooks'

export const SearchPage: FC<ISearchPageProps> = ({ data, pageContext, collectionName, root, filter, saveState = false }) => {
  let searchParams = ''

  if (typeof window !== 'undefined') {
    const { search } = window && window.location
    searchParams = new URLSearchParams(search).get('search') || ''
  }

  const frontmatter = data.markdownRemark.frontmatter
  const lowerQuery = searchParams?.toLowerCase() || ''

  const articlesToFilter = pageContext?.tag ? data.items.nodes : pageContext.content || []
  const searchItems = lowerQuery ? filter(articlesToFilter, lowerQuery) : articlesToFilter

  return (
    <>
      <MainSection>
        <MainSectionParts.FirstSection image={frontmatter.backgroundImage}>
          <h1 className={styles.mainSectionHeader}>{frontmatter.title}</h1>
        </MainSectionParts.FirstSection>
        <MainSectionParts.SecondSection
          tags={pageContext?.visibleTags}
          dontShowRectangles
          dontShowParallelepiped
          selectedTag={pageContext?.tag || ''}
          searchQuery={searchParams}
          showSearch
          root={root}
        >
          {searchParams ? (
            <PagePreviewTiles tiles={searchItems} defaultTileImage={frontmatter.defaultTileImage} root={root} />
          ) : (
            pageContext && (
              <ItemsInfiniteScroll
                data={data}
                pageContext={pageContext}
                collectionName={collectionName}
                root={root}
                saveState={saveState}
              />
            )
          )}
          {!(searchItems.length > 0) && <h2 className={cls(styles.sectionHeader, styles.blogSectionHeader)}>Nothing found.</h2>}
        </MainSectionParts.SecondSection>
      </MainSection>
    </>
  )
}

const ItemsInfiniteScroll: React.FC<IInfiniteScrollProps> = ({ data, pageContext, collectionName, root, saveState }) => {
  const [searchItems, setSearchItems] = useState({
    nextPage: 1,
    hasMore: pageContext?.hasMore ?? false,
    content: pageContext?.content ?? [],
  })

  useRestoreState(searchItems, setSearchItems, collectionName, saveState)

  useEffect(() => {
    if (saveState) localStorage.setItem('pageState', JSON.stringify({ nextPage: searchItems.nextPage }))
  }, [searchItems])

  const loadMore = () => loadMoreNews(searchItems, setSearchItems, collectionName)
  const searchItemsList = createInfiniteScroll(searchItems.content, loadMore, searchItems.hasMore, data, root)

  return (
    <>
      <section>{searchItemsList}</section>
    </>
  )
}

const loadMoreNews = (pressStateArg: ISearchItemState, setPressState: (pressState: ISearchItemState) => void, collectionName: string) => {
  const nextPageUrl = `${SITE_URL}jsonPages/${collectionName}/${pressStateArg.nextPage}.json`
  fetch(nextPageUrl)
    .then((result) => result.json())
    .then(({ hasMore, content }: IPage) => {
      setPressState({
        hasMore,
        nextPage: +pressStateArg.nextPage + 1,
        content: pressStateArg.content.concat(content),
      })
    })
    .catch(console.error)
}

const createInfiniteScroll = (items: ISearchItem[], next: () => void, hasMore: boolean, data: IData, root: string) => (
  <InfiniteScroll
    dataLength={items.length}
    next={next}
    hasMore={hasMore}
    loader={<h3 className={styles.infiniteScrollText}>Loading...</h3>}
    scrollThreshold={0.5}
    style={{ overflow: 'unset' }}
  >
    <PagePreviewTiles tiles={items} defaultTileImage={data.markdownRemark.frontmatter.defaultTileImage} root={root} saveState />
  </InfiniteScroll>
)

export default SearchPage
